import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { i18n, type OrderStatus } from '@wms/shared';
import { OrderStatusChip } from '../components/StatusChip.js';
import { StockMovementsTable } from '../components/StockMovementsTable.js';
import { useAuth } from '../lib/auth-context.js';
import { api } from '../lib/api.js';
import { formatCents, formatDate } from '../lib/format.js';

const { t } = i18n;

type PaidUntilBucket = 'green' | 'amber' | 'red' | 'unknown';

interface CustomerSelfView {
  id: string;
  code: string;
  name: string;
  status: string;
  billingMode: 'PER_PALLET_DAY' | 'FIXED_MONTHLY';
  pricingTier: { name: string; kind: string } | null;
  paidUntil: string | null;
  lastPaymentAt: string | null;
  bucket: PaidUntilBucket;
  currentCounts: Record<string, number>;
  currentHeld: number;
  storageRevenueCents: number;
  payments: {
    id: string;
    amountCents: number;
    currency: string;
    paidAt: string;
    reference: string | null;
  }[];
}

const BUCKET_CLASS: Record<PaidUntilBucket, string> = {
  green: 'bg-green-100 text-green-900 border-green-300 dark:bg-green-900/40 dark:text-green-100',
  amber: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-100',
  red: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-900/40 dark:text-red-100',
  unknown: 'bg-muted text-muted-foreground border-border',
};

interface PartnerDashboardData {
  partner: { id: string; code: string; name: string; defaultSlaWorkdays: number };
  stock: { componentCount: number; productCount: number; lowStockCount: number };
  pallets: {
    currentCounts: Record<string, number>;
    currentHeld: number;
    period: { from: string; to: string };
    arrivedInPeriod: number;
    dispatchedInPeriod: number;
    netInPeriod: number;
    storageRevenueCents: number;
  };
  orders: {
    byStatus: Record<string, number>;
    open: number;
    recent: {
      id: string;
      code: string;
      status: OrderStatus;
      placedAt: string;
      insufficientParts: boolean;
      lineCount: number;
      deliveryGroupCount: number;
    }[];
  };
}

/**
 * Tier-user landing page. A storage Customer sees a read-only summary of their
 * own account (paid-until status, recent payments, live pallet counts) via
 * `GET /customers/me`. Partner Admins / Order Placers still get the generic
 * scope card until their own self-view lands.
 */
export function MyHomePage() {
  const { user } = useAuth();
  const role = user?.roles[0];
  const isCustomer = user?.roles.some((r) => r.role === 'CUSTOMER') ?? false;
  const isPartner =
    user?.roles.some((r) => r.role === 'PARTNER_ADMIN' || r.role === 'ORDER_PLACER') ?? false;

  const [self, setSelf] = useState<CustomerSelfView | null>(null);
  const [selfError, setSelfError] = useState<string | null>(null);
  const [partner, setPartner] = useState<PartnerDashboardData | null>(null);
  const [partnerError, setPartnerError] = useState<string | null>(null);
  // Pallet-stats reporting window (YYYY-MM-DD). Empty → backend defaults to the
  // current calendar month.
  const [palletFrom, setPalletFrom] = useState('');
  const [palletTo, setPalletTo] = useState('');

  useEffect(() => {
    if (!isCustomer) return;
    api<CustomerSelfView>('/customers/me')
      .then(setSelf)
      .catch((err: unknown) => setSelfError(String(err)));
  }, [isCustomer]);

  useEffect(() => {
    if (!isPartner) return;
    const qs = new URLSearchParams();
    if (palletFrom) qs.set('from', palletFrom);
    if (palletTo) qs.set('to', palletTo);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    api<PartnerDashboardData>(`/partners/me/dashboard${suffix}`)
      .then(setPartner)
      .catch((err: unknown) => setPartnerError(String(err)));
  }, [isPartner, palletFrom, palletTo]);

  return (
    <section className="space-y-6 max-w-7xl">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">{t('my_home.title')}</h2>
        <p className="text-sm">
          {t('my_home.greeting').replace('{{name}}', user?.displayName ?? '')}
        </p>
      </div>

      {isCustomer ? <CustomerSummary self={self} error={selfError} /> : null}
      {isPartner ? (
        <PartnerDashboard
          data={partner}
          error={partnerError}
          from={palletFrom}
          to={palletTo}
          onFrom={setPalletFrom}
          onTo={setPalletTo}
        />
      ) : null}

      {!isCustomer && !isPartner ? (
        <>
          <div className="rounded border border-border p-4 text-sm space-y-2">
            <div>
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('my_home.role')}:
              </span>{' '}
              <span className="font-mono">{role?.role}</span>
            </div>
            {role?.scopeId ? (
              <div>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('my_home.scope')}:
                </span>{' '}
                <span className="font-mono text-xs">{role.scopeId}</span>
              </div>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">{t('my_home.coming_soon')}</p>
        </>
      ) : null}
    </section>
  );
}

function CustomerSummary({ self, error }: { self: CustomerSelfView | null; error: string | null }) {
  if (error) {
    return <div className="text-sm text-red-700 dark:text-red-300">{error}</div>;
  }
  if (!self) {
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-medium">{self.name}</div>
            <div className="font-mono text-xs text-muted-foreground">{self.code}</div>
          </div>
          <span
            className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs font-medium ${BUCKET_CLASS[self.bucket]}`}
          >
            {t(`customer.paid_until.tone.${self.bucket}`)}
          </span>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">{t('customer.col.billing_mode')}</dt>
          <dd className="text-right">{t(`customer.billing.${self.billingMode}`)}</dd>

          <dt className="text-muted-foreground">{t('customer.col.pricing_tier')}</dt>
          <dd className="text-right">{self.pricingTier?.name ?? '—'}</dd>

          <dt className="text-muted-foreground">{t('customer.col.paid_until')}</dt>
          <dd className="text-right">
            {self.paidUntil ? formatDate(self.paidUntil) : t('customer.paid_until.never')}
          </dd>

          <dt className="text-muted-foreground">{t('customer.col.last_payment')}</dt>
          <dd className="text-right">{formatDate(self.lastPaymentAt)}</dd>

          <dt className="text-muted-foreground">{t('my_home.customer.held_now')}</dt>
          <dd className="text-right font-medium">{self.currentHeld}</dd>

          <dt className="text-muted-foreground">{t('my_home.storage_revenue_month')}</dt>
          <dd className="text-right font-medium tabular-nums">
            {formatCents(self.storageRevenueCents)}
          </dd>
        </dl>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('my_home.customer.recent_payments')}
        </h3>
        {self.payments.length === 0 ? (
          <div className="rounded border border-dashed border-border p-3 text-center text-sm text-muted-foreground">
            {t('payment.list.empty')}
          </div>
        ) : (
          <ul className="space-y-1">
            {self.payments.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between rounded border border-border px-3 py-2 text-sm"
              >
                <span className="text-muted-foreground">{formatDate(p.paidAt)}</span>
                <span className="font-medium">{formatCents(p.amountCents, p.currency)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border border-border p-3 text-center">
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

function PartnerDashboard({
  data,
  error,
  from,
  to,
  onFrom,
  onTo,
}: {
  data: PartnerDashboardData | null;
  error: string | null;
  from: string;
  to: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}) {
  if (error) {
    return <div className="text-sm text-red-700 dark:text-red-300">{error}</div>;
  }
  if (!data) {
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-medium">{data.partner.name}</div>
            <div className="font-mono text-xs text-muted-foreground">{data.partner.code}</div>
          </div>
          <span className="text-xs text-muted-foreground">
            {t('partner.col.sla')}: {data.partner.defaultSlaWorkdays} {t('partner.col.sla_unit')}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Stat label={t('my_home.partner.open_orders')} value={data.orders.open} />
          <Link to={`/partners/${data.partner.id}/low-stock`} className="block">
            <Stat label={t('my_home.partner.low_stock')} value={data.stock.lowStockCount} />
          </Link>
          <Stat label={t('my_home.partner.products')} value={data.stock.productCount} />
        </div>
        <div className="text-xs text-muted-foreground">
          {t('my_home.partner.components')}: {data.stock.componentCount}
        </div>
      </div>

      {/* Pallet activity — current holdings + movement over a chosen period. */}
      <div className="rounded-md border border-border p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('my_home.partner.pallets')}
          </h3>
          <div className="flex items-center gap-2 text-xs">
            <label className="flex items-center gap-1">
              <span className="text-muted-foreground">{t('common.from')}</span>
              <input
                type="date"
                value={from}
                onChange={(e) => onFrom(e.target.value)}
                className="rounded border border-border bg-background px-2 py-1"
              />
            </label>
            <label className="flex items-center gap-1">
              <span className="text-muted-foreground">{t('common.to')}</span>
              <input
                type="date"
                value={to}
                onChange={(e) => onTo(e.target.value)}
                className="rounded border border-border bg-background px-2 py-1"
              />
            </label>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label={t('my_home.partner.pallets_held')} value={data.pallets.currentHeld} />
          <Stat label={t('my_home.partner.pallets_arrived')} value={data.pallets.arrivedInPeriod} />
          <Stat
            label={t('my_home.partner.pallets_dispatched')}
            value={data.pallets.dispatchedInPeriod}
          />
          <Stat
            label={t('my_home.partner.pallets_net')}
            value={`${data.pallets.netInPeriod > 0 ? '+' : ''}${data.pallets.netInPeriod}`}
          />
        </div>
        <div className="rounded border border-border bg-muted/40 px-3 py-2 text-sm">
          <span className="text-muted-foreground">{t('my_home.storage_revenue')}: </span>
          <span className="font-semibold tabular-nums">
            {formatCents(data.pallets.storageRevenueCents)}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('my_home.partner.recent_orders')}
        </h3>
        {data.orders.recent.length === 0 ? (
          <div className="rounded border border-dashed border-border p-3 text-center text-sm text-muted-foreground">
            {t('my_home.partner.no_orders')}
          </div>
        ) : (
          <ul className="space-y-1">
            {data.orders.recent.map((o) => (
              <li key={o.id}>
                <Link
                  to={`/orders/${o.id}`}
                  className="flex items-center justify-between gap-2 rounded border border-border px-3 py-2 text-sm hover:bg-muted"
                >
                  <span className="min-w-0">
                    <span className="font-mono text-xs">{o.code}</span>
                    {o.insufficientParts ? <span className="ml-2 text-red-600">●</span> : null}
                  </span>
                  <span className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-muted-foreground">{formatDate(o.placedAt)}</span>
                    <OrderStatusChip status={o.status} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-md border border-border p-4">
        <StockMovementsTable partnerId={data.partner.id} reloadKey={0} showExport={false} />
      </div>
    </div>
  );
}
